import { useState } from "react";
import { IRole } from "../../Models/IRole";
import { emailListError, IUser } from "../../Models/IUser";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { Btn, CheckBox, Dialog, Field, Msg, Switch, TextInput } from "./SetupUi";

// «Nuovo utente» e «Modifica utente» (risposta 7): email (fino a 5), notifiche, Attivo e ruoli in una
// finestra. Stesse chiamate di oggi: crea o aggiorna l'utente, poi aggiunge e toglie i ruoli.

interface IUserDraft {
  identifier: string;
  password: string;
  email: string;
  emailNotifications: boolean;
  enabled: boolean;
  roles: number[];
}

const UserDialog = (props: {
  user?: IUser;
  users: IUser[];
  roles: IRole[];
  isMe: boolean;
  onClose: () => void;
  // relogin: ho cambiato i miei ruoli, si esce e si rientra (come oggi)
  onSaved: (message: string, relogin: boolean) => void;
}) => {
  const u = props.user;
  const [orig] = useState<IUserDraft>(() => ({
    identifier: u?.identifier ?? "",
    password: "",
    email: u?.email ?? "",
    emailNotifications: u ? u.emailNotifications !== false : true,
    enabled: u ? !!u.enabled : true,
    roles: (u?.roles ?? []).map((r) => r.id),
  }));
  const [d, setD] = useState<IUserDraft>(orig);
  const [saving, setSaving] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (patch: Partial<IUserDraft>) => setD((prev) => ({ ...prev, ...patch }));

  const mailErr = emailListError(d.email) ?? null;
  const sorted = (x: IUserDraft) => ({ ...x, roles: [...x.roles].sort((a, b) => a - b) });
  const changed = !u || JSON.stringify(sorted(d)) !== JSON.stringify(sorted(orig));
  const rolesChanged = !!u && JSON.stringify([...d.roles].sort()) !== JSON.stringify([...orig.roles].sort());
  const why = saving
    ? "Salvataggio in corso..."
    : !u && (!d.identifier.trim() || !d.password)
    ? "Scrivi nome utente e password."
    : !u && props.users.some((x) => x.identifier === d.identifier.trim())
    ? "Nome utente già in uso."
    : mailErr
    ? mailErr + "."
    : !changed
    ? "Nessuna modifica da salvare."
    : null;

  const save = async () => {
    if (why) return;
    setSaving(true);
    setErr(null);
    const fieldsChanged =
      !u || d.email !== orig.email || d.emailNotifications !== orig.emailNotifications || d.enabled !== orig.enabled;
    let id = u?.id;
    if (fieldsChanged) {
      const payload: any = u
        ? { id: u.id, email: d.email.trim() || null, emailNotifications: d.emailNotifications, enabled: d.enabled }
        : {
            identifier: d.identifier.trim(),
            password: d.password,
            email: d.email.trim() || null,
            emailNotifications: d.emailNotifications,
            enabled: d.enabled,
          };
      const r: IAPIResponse = await ApiService.UsersController.CreateOrUpdateUser(payload);
      if (r.error !== null) {
        setSaving(false);
        setErr(u ? "L'utente non è stato salvato. Riprova." : "Il server non ha creato l'utente. Riprova.");
        return;
      }
      if (!u) id = r.parsed?.id;
    }
    const failed: string[] = [];
    if (id !== undefined) {
      const roleName = (rid: number) => props.roles.find((r) => r.id === rid)?.type.toUpperCase() ?? String(rid);
      for (const rid of d.roles.filter((x) => !orig.roles.includes(x))) {
        const r: IAPIResponse = await ApiService.UsersController.AddUserRole(id, rid);
        if (r.error !== null) failed.push(roleName(rid));
      }
      for (const rid of orig.roles.filter((x) => !d.roles.includes(x))) {
        const r: IAPIResponse = await ApiService.UsersController.DeleteUserRole(id, rid);
        if (r.error !== null) failed.push(roleName(rid));
      }
    }
    const name = d.identifier.trim();
    if (failed.length) {
      props.onSaved(name + (u ? " salvato" : " creato") + ", ma i ruoli " + failed.join(", ") + " non sono stati salvati.", false);
      return;
    }
    props.onSaved(u ? name + " salvato." : "Utente " + name + " creato.", props.isMe && rolesChanged);
  };

  const title = u ? "Modifica " + u.identifier : "Nuovo utente";
  return (
    <Dialog
      title={title}
      ariaTitle={title}
      wide
      why={why}
      onClose={() => {
        if (!saving) props.onClose();
      }}
      footer={
        <>
          <Btn text="Annulla" why={saving ? "Salvataggio in corso" : null} onClick={props.onClose} />
          <Btn text={saving ? "Salvataggio..." : u ? "Salva" : "Crea utente"} primary spin={saving} why={why} onClick={save} />
        </>
      }
    >
      <div className="sec">
        {u ? (
          <Field label="Nome utente" hint="Il nome utente non si cambia.">
            <TextInput value={d.identifier} readOnly onChange={() => {}} />
          </Field>
        ) : (
          <div className="grid2">
            <Field label="Nome utente" required>
              <TextInput value={d.identifier} autoFocus onChange={(v) => set({ identifier: v })} />
            </Field>
            <Field label="Password" required>
              <TextInput value={d.password} type="password" onChange={(v) => set({ password: v })} />
            </Field>
          </div>
        )}
        <Field label="Email per ricevere gli allarmi" err={mailErr} hint="Fino a 5 indirizzi, separati da virgola.">
          <TextInput value={d.email} bad={!!mailErr} placeholder="una o più, separate da virgola" onChange={(v) => set({ email: v })} />
        </Field>
        <div className="grid2">
          <div className="fld">
            <span className="lab">Attivo</span>
            <Switch on={d.enabled} label={d.enabled ? "Attivo: può entrare" : "Spento: non entra"} onClick={() => set({ enabled: !d.enabled })} />
            {!d.enabled && <span className="hint">Non entra e non riceve email.</span>}
          </div>
          <div className="fld">
            <span className="lab">Email degli allarmi</span>
            {!d.enabled ? (
              <>
                <Switch on={false} label="Non arrivano" why="L'utente è spento: le email tornano quando lo riaccendi." />
                <span className="hint">{d.email.trim() && d.emailNotifications ? "Tornano quando riaccendi l'utente." : "L'utente è spento."}</span>
              </>
            ) : (
              <>
                <Switch
                  on={!!d.email.trim() && d.emailNotifications}
                  label={d.email.trim() && d.emailNotifications ? "Accese" : "Spente"}
                  why={d.email.trim() ? null : "Serve almeno un'email"}
                  onClick={() => set({ emailNotifications: !d.emailNotifications })}
                />
                {!d.email.trim() && <span className="hint">Serve almeno un'email.</span>}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="sec">
        <div className="st">Ruoli</div>
        <div className="olist">
          {props.roles.map((r) => {
            const on = d.roles.includes(r.id);
            return (
              <button
                type="button"
                key={r.id}
                className="orow"
                aria-pressed={on}
                onClick={() => set({ roles: on ? d.roles.filter((x) => x !== r.id) : [...d.roles, r.id] })}
              >
                <CheckBox on={on} />
                <span className="grow">{r.type.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
        {props.isMe && rolesChanged && <Msg kind="warn">Stai cambiando i tuoi ruoli: al salvataggio esci da SSL Cloud e rientri.</Msg>}
      </div>
      {err && <Msg kind="err">{err}</Msg>}
    </Dialog>
  );
};

export default UserDialog;
